import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  FaYoutube,
  FaTwitter,
  FaLinkedinIn,
  FaPencilAlt,
  FaGithub,
  FaEnvelope,
  FaGraduationCap
} from 'react-icons/fa'
import Link from '../Link'
import Image from '../Image'

const Container = styled.div`
  display: inline-flex;
  vertical-align: top;
  flex-direction: column;
  height: 100%;

  a:hover {
    > div {
      border-color: ${({ theme }) => theme.colors.interactive};
    }
  }
`

const ProfileImage = styled.div`
  overflow: hidden;
  width: 14rem;
  height: 14rem;
  border-radius: 10rem;
  padding: 0.6rem;
  border: 0.1rem solid ${({ theme }) => theme.colors.pageRule};
  transition: border-color 0.2s ease-in-out;
  margin: 0 auto;
  display: block;
  position: relative;

  img {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    margin: 0;
    padding: 0.6rem;
  }
`

const Title = styled.p`
  margin: 1rem 0 0;
`

const JobTitles = styled.p`
  text-transform: uppercase;
  font-size: 1.2rem;
  font-weight: 600;

  small {
    display: block;
    line-height: normal;
  }
`

const ProfileLinks = styled.ul`
  list-style: none;
  margin: 0;
  width: 100%;
  text-align: center;
`

const LinksContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  min-height: 3rem;
`

const ProfileLink = styled.li`
  display: inline-block;
  margin: 0;

  a {
    padding: 1rem;
    color: ${({ theme }) => theme.colors.darkText};
    border-radius: 3rem;
    width: 3rem;
    height: 3rem;
    display: block;
    transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out;
    position: relative;

    &:hover {
      color: ${({ theme }) => theme.colors.text};
      background-color: ${({ theme }) => theme.colors.interactive};
    }

    > svg {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }

  @media screen and (max-width: 800px) {
    a {
      width: 4rem;
      height: 4rem;
    }
  }
`

const TeamMember = ({
  title,
  display_name: displayName,
  slug,
  thumbnail,
  content
}) => (
  <Container>
    <Link
      tracking={{ label: `team_member_title_${slug}` }}
      href={`/team/${slug}`}
    >
      <ProfileImage>
        <Image src={thumbnail} size={150} alt={title} />
      </ProfileImage>
      <Title>{displayName || title}</Title>
    </Link>
    <JobTitles>
      {content.job_titles.map((jobTitle, index) => (
        <Fragment key={jobTitle.name}>
          {index === 0 && jobTitle.name}
          {index > 0 && <small>{jobTitle.name}</small>}
        </Fragment>
      ))}
    </JobTitles>
    <LinksContainer>
      <ProfileLinks>
        {content.profile_links.youtube && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_youtube_${slug}` }}
              href={content.profile_links.youtube}
            >
              <FaYoutube />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.email && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_email_${slug}` }}
              href={`mailto:${content.profile_links.email}`}
            >
              <FaEnvelope />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.twitter && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_twitter_${slug}` }}
              href={content.profile_links.twitter}
            >
              <FaTwitter />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.linkedin && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_linkedin_${slug}` }}
              href={content.profile_links.linkedin}
            >
              <FaLinkedinIn />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.github && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_github_${slug}` }}
              href={content.profile_links.github}
            >
              <FaGithub />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.blog && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_blog_${slug}` }}
              href={content.profile_links.blog}
            >
              <FaPencilAlt />
            </Link>
          </ProfileLink>
        )}
        {content.profile_links.research && (
          <ProfileLink>
            <Link
              tracking={{ label: `team_member_research_${slug}` }}
              href={content.profile_links.research}
            >
              <FaGraduationCap />
            </Link>
          </ProfileLink>
        )}
      </ProfileLinks>
    </LinksContainer>
  </Container>
)

TeamMember.propTypes = {
  title: PropTypes.string.isRequired,
  display_name: PropTypes.string,
  thumbnail: PropTypes.string.isRequired,
  content: PropTypes.shape({
    job_titles: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired
      })
    ).isRequired,
    profile_links: PropTypes.shape({
      youtube: PropTypes.string,
      email: PropTypes.string,
      linkedin: PropTypes.string,
      twitter: PropTypes.string,
      github: PropTypes.string,
      blog: PropTypes.string,
      research: PropTypes.string
    }).isRequired
  }).isRequired,
  slug: PropTypes.string.isRequired
}

export default TeamMember
